/**
 * The physician review screen (CLI-002 to CLI-007): intake, observations used, the draft with
 * each ingredient's rationale, then approve, edit with a reason code, or decline with a note.
 */
import * as stylex from "@stylexjs/stylex";
import type { FormEvent } from "react";
import { useState } from "react";
import type { AdminReview, AdminReviewComponent, AdminReviewObservation, AdminReviewViolation, ReviewDecision, ReviewEditInput } from "@lattice/core";
import { biomarker, DECLINE_REASONS, isBiomarkerCode, isDeclineReason, isReviewEditChange, isReviewEditReason, parseReviewEdits, REVIEW_EDIT_CHANGES, REVIEW_EDIT_REASONS, ROUTES, STAFF_REASON_MAX } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { api } from "./api.ts";
import { Context, RecordLink, Truncate } from "./bits.tsx";
import { publishNotice } from "./notice.ts";
import type { Column } from "./layout.tsx";
import { Facts, PageHead, Panel, Section, Split, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { href } from "./router.ts";
import { Button, ErrorText, Field, Link, Select, Status } from "./ui.tsx";
import { day, DECLINE_REASON, DIET, EDIT_CHANGE, EDIT_REASON, EXERCISE, GOAL, refusal, waited } from "./words.ts";
const VALUE = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 3
});
const SEX = {
  female: "Female",
  male: "Male"
};
type Observation = AdminReviewObservation;
function flagOf(o: Observation) {
  switch (o.flag) {
    case "low":
      return <Status tone="warn">Low</Status>;
    case "high":
      return <Status tone="warn">High</Status>;
    case "abnormal":
      return <Status tone="danger">Abnormal</Status>;
    case "normal":
      return <span className="x1jty25b">In range</span>;
    case null:
      return null;
  }
}
function valueOf(o: Observation) {
  const value = o.valueNumeric === null ? o.valueText ?? "" : VALUE.format(o.valueNumeric);
  const outside = o.flag !== null && o.flag !== "normal";
  return <span {...{
    0: {},
    1: {
      className: "xqu410z"
    }
  }[!!outside << 0]}>{value}</span>;
}
function joined(values: readonly string[]): string {
  return values.length === 0 ? "None" : values.join(", ");
}
function labeled<K extends string>(value: K | null, labels: Readonly<Record<K, string>>): string {
  if (value === null) return "Not recorded";
  return labels[value];
}
function Intake({
  review
}: {
  readonly review: AdminReview;
}) {
  const {
    intake
  } = review;
  const training = intake.exerciseDaysPerWeek === null ? "Not recorded" : `${intake.exerciseDaysPerWeek} days, ${labeled(intake.exerciseType, EXERCISE)}`;
  return <Section title="Intake">
      <Facts items={[["Age", intake.age === null ? "Not recorded" : String(intake.age)], ["Sex", intake.sex === null ? "Not recorded" : SEX[intake.sex]], ["Height", intake.heightCm === null ? "Not recorded" : `${intake.heightCm} cm`], ["Weight", intake.weightKg === null ? "Not recorded" : `${intake.weightKg} kg`], ["Conditions", joined(intake.conditions)], ["Medications", joined(intake.medications)], ["Supplements", intake.supplements.length === 0 ? "None" : intake.supplements.map(s => s.dailyDose === null ? s.name : `${s.name} ${s.dailyDose}`).join(", ")], ["Allergies", joined(intake.allergies)], ["Diet", labeled(intake.diet, DIET)], ["Training", training], ["Goal", labeled(intake.goal, GOAL)]]} />
    </Section>;
}
const OBS_COLUMNS: readonly Column<Observation>[] = [{
  label: "Marker",
  cell: o => o.name
}, {
  label: "Value",
  numeric: true,
  cell: valueOf
}, {
  label: "Unit",
  nowrap: true,
  cell: o => <span className="x1jty25b">{o.unit ?? ""}</span>
}, {
  label: "Flag",
  nowrap: true,
  cell: flagOf
}, {
  label: "Reference",
  phoneLabel: true,
  nowrap: true,
  cell: o => o.referenceText ?? <span className="x1jty25b">None printed</span>
}, {
  label: "Collected",
  numeric: true,
  phoneLabel: true,
  cell: o => day(o.collectedOn)
}, {
  label: "Page",
  numeric: true,
  phoneLabel: true,
  cell: o => o.sourcePage
}, {
  label: "Confidence",
  numeric: true,
  phoneLabel: true,
  cell: o => VALUE.format(o.confidence)
}];
const DRAFT_COLUMNS: readonly Column<AdminReviewComponent>[] = [{
  label: "Ingredient",
  cell: c => c.ingredientName
}, {
  label: "Dose",
  numeric: true,
  cell: c => VALUE.format(c.dose)
}, {
  label: "Unit",
  nowrap: true,
  cell: c => <span className="x1jty25b">{c.unit}</span>
}, {
  label: "Rationale",
  phoneLabel: true,
  cell: c => c.rationale
}];
function emptyEdit(): ReviewEditInput {
  return {
    ingredientId: "",
    change: "dose_changed",
    fromDose: null,
    toDose: null,
    reasonCode: "other",
    note: null
  };
}
function EditForm({
  review,
  onChange
}: {
  readonly review: AdminReview;
  readonly onChange: (edits: readonly ReviewEditInput[]) => void;
}) {
  const [rows, setRows] = useState<readonly ReviewEditInput[]>([]);
  const ingredients = [...review.components.map(c => ({
    value: c.ingredientId,
    label: c.ingredientName
  })), ...review.catalog.flatMap(item => review.components.some(c => c.ingredientId === item.id) ? [] : [{
    value: item.id,
    label: item.name
  }])];
  function update(next: readonly ReviewEditInput[]) {
    setRows(next);
    onChange(next);
  }
  return <Section title="Edits" count={rows.length}>
      {rows.length === 0 ? <p className="x1ghz6dp x1jty25b">Approve as drafted, or add an edit with a reason code.</p> : null}
      {rows.map((row, index) => <Panel key={`${row.ingredientId}-${index}`} padded>
          <div className="x78zum5 xdt5ytf x1r0x45p">
            <Select id={`edit-ingredient-${index}`} label="Ingredient" value={row.ingredientId} options={ingredients} onChange={ingredientId => update(rows.map((current, at) => at === index ? {
          ...current,
          ingredientId
        } : current))} />
            <Select id={`edit-change-${index}`} label="Change" value={row.change} options={REVIEW_EDIT_CHANGES.map(change => ({
          value: change,
          label: EDIT_CHANGE[change]
        }))} onChange={value => update(rows.map((current, at) => at === index && isReviewEditChange(value) ? {
          ...current,
          change: value
        } : current))} />
            {row.change === "removed" ? null : <Field id={`edit-dose-${index}`} label="Dose" value={row.toDose === null ? "" : String(row.toDose)} onChange={text => update(rows.map((current, at) => at === index ? {
          ...current,
          toDose: text.trim() === "" ? null : Number(text)
        } : current))} />}
            <Select id={`edit-reason-${index}`} label="Reason" value={row.reasonCode} options={REVIEW_EDIT_REASONS.map(reason => ({
          value: reason,
          label: EDIT_REASON[reason]
        }))} onChange={value => update(rows.map((current, at) => at === index && isReviewEditReason(value) ? {
          ...current,
          reasonCode: value
        } : current))} />
            <Field id={`edit-note-${index}`} label="Note" value={row.note ?? ""} onChange={note => update(rows.map((current, at) => at === index ? {
          ...current,
          note: note.trim() === "" ? null : note
        } : current))} multiline maxLength={STAFF_REASON_MAX} />
            <Button tone="quiet" onClick={() => update(rows.filter((_, at) => at !== index))}>
              Remove this edit
            </Button>
          </div>
        </Panel>)}
      <div className="x78zum5 x1a02dak x1173whf">
        <Button tone="quiet" onClick={() => update([...rows, emptyEdit()])}>
          Add an edit
        </Button>
      </div>
    </Section>;
}
function Decision({
  review,
  edits
}: {
  readonly review: AdminReview;
  readonly edits: readonly ReviewEditInput[];
}) {
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState<readonly AdminReviewViolation[] | null>(null);
  const [phase, setPhase] = useState<"idle" | "saving" | "saved">("idle");
  const [savedAs, setSavedAs] = useState<"approved" | "declined" | null>(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const parsedEdits = parseReviewEdits(edits);
  const canApprove = parsedEdits !== null && (edits.length === 0 || parsedEdits.length === edits.length);
  async function sendApprove() {
    if (parsedEdits === null || phase === "saving") return;
    setPhase("saving");
    setError(null);
    setFailed(null);
    publishNotice("saving", "Saving");
    const result = await api<ReviewDecision>(ROUTES.adminApproveReview, {
      params: {
        reviewId: review.id
      },
      body: parsedEdits.length === 0 ? {} : {
        edits: parsedEdits
      }
    });
    if (!result.ok) {
      setPhase("idle");
      setError(refusal(result.error));
      setFailed(result.violations);
      publishNotice("failed", refusal(result.error));
      return;
    }
    setPhase("saved");
    setSavedAs("approved");
    publishNotice("saved", "Saved. The formula is signed.");
  }
  async function sendDecline(event: FormEvent) {
    event.preventDefault();
    if (!isDeclineReason(reason) || phase === "saving") return;
    setPhase("saving");
    setError(null);
    setFailed(null);
    publishNotice("saving", "Saving");
    const result = await api<ReviewDecision>(ROUTES.adminDeclineReview, {
      params: {
        reviewId: review.id
      },
      body: {
        reason,
        note
      }
    });
    if (!result.ok) {
      setPhase("idle");
      setError(refusal(result.error));
      publishNotice("failed", refusal(result.error));
      return;
    }
    setPhase("saved");
    setSavedAs("declined");
    publishNotice("saved", "Saved. The review is declined.");
  }
  if (savedAs !== null) return <Panel padded>
        <div className="x78zum5 xdt5ytf x1r0x45p">
          <p role="status" className="x1ghz6dp x1jty25b">
            {savedAs === "approved" ? "Saved. The formula is signed." : "Saved. The review is declined."}
          </p>
          <Link to={href.reviews()}>Back to reviews</Link>
        </div>
      </Panel>;
  if (review.decision !== null) return <Panel padded>
        <p className="x1ghz6dp x1jty25b">
          Already {review.decision}. Formula version{" "}
          <Truncate value={review.resultVersionId ?? review.formulaVersionId} />
        </p>
      </Panel>;
  return <div className="x78zum5 xdt5ytf x1r0x45p">
      <Panel padded>
        <div className="x78zum5 xdt5ytf x1r0x45p">
          <h2 className="x1ghz6dp x1rf3zrh x117lpx5 x1e5flnj">Approve</h2>
          <p className="x1ghz6dp x1jty25b">
            {edits.length === 0 ? "Signs the drafted formula as it is." : "Creates a physician version with these edits, then signs that."}
          </p>
          <Button disabled={phase === "saving" || !canApprove} onClick={() => void sendApprove()}>
            {phase === "saving" ? "Saving" : "Approve"}
          </Button>
        </div>
      </Panel>
      <Panel padded>
        <form onSubmit={sendDecline} className="x78zum5 xdt5ytf x1r0x45p">
          <h2 className="x1ghz6dp x1rf3zrh x117lpx5 x1e5flnj">Decline</h2>
          <p className="x1ghz6dp x1jty25b">
            A listed reason and a note for the customer are required.
          </p>
          <Select id="decline-reason" label="Reason" value={reason} options={DECLINE_REASONS.map(id => ({
          value: id,
          label: DECLINE_REASON[id]
        }))} onChange={setReason} />
          <Field id="decline-note" label="Note for the customer" value={note} onChange={setNote} multiline maxLength={STAFF_REASON_MAX} />
          <Button type="submit" tone="danger" disabled={phase === "saving" || !isDeclineReason(reason) || note.trim() === ""}>
            {phase === "saving" ? "Saving" : "Decline"}
          </Button>
        </form>
      </Panel>
      {error === null ? null : <ErrorText>{error}</ErrorText>}
      {failed === null || failed.length === 0 ? null : <ul className="x1ghz6dp xa9f7qn">
          {failed.map(v => <li key={`${v.kind}-${v.note}`}>{v.note}</li>)}
        </ul>}
    </div>;
}
function Screen({
  review
}: {
  readonly review: AdminReview;
}) {
  const [edits, setEdits] = useState<readonly ReviewEditInput[]>([]);
  return <>
      <PageHead back={<Link to={href.reviews()}>Back to reviews</Link>} title={review.patientName ?? "Name not given"} meta={<>
            <span>{review.pastTarget ? "Past 24 hours" : waited(review.waitingMs)}</span>
            <RecordLink to={href.patient(review.patientId)} relation="Patient" label={review.patientName ?? "Patient record"} />
          </>} />
      <Split side={<>
            <Context title="This review" who={review.patientName ?? "Name not given"} role={review.pastTarget ? "Past 24 hours" : waited(review.waitingMs)} can="You can approve, edit or decline. You cannot change billing." links={[{
        relation: "Patient",
        label: review.patientName ?? "Patient record",
        to: href.patient(review.patientId)
      }, {
        relation: "Queue",
        label: "Back to reviews",
        to: href.reviews()
      }]} />
            <Intake review={review} />
            <Decision review={review} edits={edits} />
          </>} main={<>
            {review.flags.length === 0 ? null : <Section title="Flags" count={review.flags.length}>
                <ul className="x1ghz6dp xa9f7qn">
                  {review.flags.map(flag => <li key={`${flag.name}-${flag.flag}`}>
                      {flag.name}: {flag.flag}
                    </li>)}
                </ul>
              </Section>}
            {review.missingMarkers.length === 0 ? null : <Section title="Missing markers" count={review.missingMarkers.length}>
                <ul className="x1ghz6dp xa9f7qn">
                  {review.missingMarkers.map(code => <li key={code}>{isBiomarkerCode(code) ? biomarker(code).name : code}</li>)}
                </ul>
              </Section>}
            {review.violations.length === 0 ? null : <Section title="Constraint results" count={review.violations.length}>
                <ul className="x1ghz6dp xa9f7qn">
                  {review.violations.map(v => <li key={`${v.kind}-${v.ruleId ?? v.note}`}>
                      <Status tone={v.severity === "block" ? "danger" : "warn"}>
                        {v.severity === "block" ? "Block" : "Warning"}
                      </Status>{" "}
                      {v.note}
                    </li>)}
                </ul>
              </Section>}
            <Section title="Observations used" count={review.observations.length}>
              <Table label="Observations used" rows={review.observations} rowKey={o => o.id} empty="No observations were used for this draft." columns={OBS_COLUMNS} />
            </Section>
            <Section title="Draft" count={review.components.length}>
              <Table label="Draft formula" rows={review.components} rowKey={c => c.ingredientId} empty="No ingredients in this draft." columns={DRAFT_COLUMNS} />
            </Section>
            {review.decision === null ? <EditForm review={review} onChange={setEdits} /> : null}
          </>} />
    </>;
}
export function ReviewScreen({
  id
}: {
  readonly id: string;
}) {
  const [loaded] = useLoad(`review-${id}`, () => api<AdminReview>(ROUTES.adminReview, {
    params: {
      reviewId: id
    }
  }));
  if (loaded.state === "loading") return <PageHead title="Opening review" />;
  if (loaded.state === "failed") return <>
        <PageHead back={<Link to={href.reviews()}>Back to reviews</Link>} title="Review" />
        <ErrorText>{refusal(loaded.error)}</ErrorText>
      </>;
  return <Screen review={loaded.data} />;
}